import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { api, errMsg } from "../lib/api";
import { ImageInput } from "./ImageInput";
import { PasswordForm } from "./PasswordForm";
import { ListsEditor } from "./ListsEditor";
import { GangSettings } from "./GangSettings";

const GROUPS = [
  ["Business Info", [["business_name", "Business name"], ["tagline", "Tagline", true], ["phone", "Phone"], ["email", "Public email"], ["location", "Location"], ["hours", "Hours (optional)"], ["facebook_url", "Facebook URL (optional)"], ["instagram_url", "Instagram URL (optional)"]]],
  ["Home Hero", [["hero_script_1", "Script line 1"], ["hero_script_2", "Script line 2"], ["hero_subtitle", "Subtitle"], ["hero_strip", "Red strip text"]]],
  ["About Page", [["about_intro", "Intro", true], ["about_title", "Section title"], ["about_body", "Body (blank line between paragraphs)", true]]],
  ["Pricing Page", [["pricing_intro", "Intro", true], ["bulk_title", "Bulk banner title"], ["bulk_text", "Bulk banner text"], ["pricing_included", "Included with pricing", true], ["gang_sheet_text", "Gang sheet text", true]]],
  ["Blank Apparel Links", [["sanmar_url", "SanMar URL"], ["ss_url", "S&S Activewear URL"]]],
];

const Field = ({ k, label, long, s, set }) => (
  <div className={long ? "sm:col-span-2" : ""}>
    <label className="label">{label}</label>
    {long ? <textarea className="field min-h-[90px]" value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`admin-settings-${k}`} />
      : <input className="field" value={s[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`admin-settings-${k}`} />}
  </div>
);

const PoliciesEditor = ({ policies, onChange }) => (
  <div className="space-y-3">
    {policies.map((p, i) => (
      <div key={i} className="rounded-lg border border-white/10 p-3" data-testid={`admin-policy-${i}`}>
        <div className="flex gap-2">
          <input className="field" value={p.title} onChange={(e) => onChange(policies.map((x, n) => (n === i ? { ...x, title: e.target.value } : x)))} data-testid={`admin-policy-title-${i}`} />
          <button onClick={() => onChange(policies.filter((_, n) => n !== i))} className="p-2 text-slate-500 hover:text-red-400" data-testid={`admin-policy-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
        <textarea className="field mt-2 min-h-[80px]" placeholder="One rule per line" value={p.items.join("\n")}
          onChange={(e) => onChange(policies.map((x, n) => (n === i ? { ...x, items: e.target.value.split("\n") } : x)))} data-testid={`admin-policy-items-${i}`} />
      </div>
    ))}
    <button onClick={() => onChange([...policies, { title: "New Policy", items: [] }])} className="flex items-center gap-1 text-sm text-[#60A5FA]" data-testid="admin-policy-add"><Plus className="h-4 w-4" />Add policy section</button>
  </div>
);

export default function SettingsAdmin() {
  const { settings, refresh } = useSite();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings && !s) setS(settings); }, [settings, s]);
  if (!s) return null;
  const set = (k, v) => setS({ ...s, [k]: v });

  const saveAll = async () => {
    setBusy(true);
    try {
      const clean = { ...s, pricing_notes: s.pricing_notes.filter((x) => x.trim()), policies: s.policies.map((p) => ({ ...p, items: p.items.filter((x) => x.trim()) })),
        item_types: (s.item_types || []).filter((x) => x.trim()), placements: (s.placements || []).filter((x) => x.trim()), quote_services: (s.quote_services || []).filter((x) => x.trim()), design_fonts: (s.design_fonts || []).map((x) => x.trim()).filter(Boolean), gang_sizes: (s.gang_sizes || []).filter((z) => z.length > 0) };
      const { data } = await api.put("/admin/settings", clean);
      setS(data);
      refresh();
      toast.success("Site settings saved");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl space-y-6 pb-24">
      <h1 className="font-display text-4xl font-black uppercase">Site Settings</h1>
      {GROUPS.map(([title, fields]) => (
        <section key={title} className="panel p-6">
          <h2 className="red-title mb-4 text-xl">{title}</h2>
          <div className="grid gap-4 sm:grid-cols-2">{fields.map(([k, label, long]) => <Field key={k} k={k} label={label} long={long} s={s} set={set} />)}</div>
          {title === "Home Hero" && (
            <div className="mt-4 space-y-4">
              <div><label className="label">Hero background image</label><ImageInput value={s.hero_image} onChange={(v) => set("hero_image", v)} testId="admin-settings-hero-image" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Intro animation speed</label>
                  <select className="field" value={s.hero_anim_speed || "0.8"} onChange={(e) => set("hero_anim_speed", e.target.value)} data-testid="admin-settings-hero-anim-speed">
                    <option value="0.55">Fast</option><option value="0.8">Normal</option><option value="1.2">Slow</option>
                  </select>
                </div>
                <label className="flex items-center gap-3 self-end pb-3 text-sm">
                  <input type="checkbox" className="h-4 w-4 accent-[#E5202A]" checked={s.hero_anim_replay !== false} onChange={(e) => set("hero_anim_replay", e.target.checked)} data-testid="admin-settings-hero-anim-replay" />
                  Replay when visitors scroll back to the top
                </label>
              </div>
            </div>
          )}
          {title === "About Page" && <div className="mt-4"><label className="label">About photo</label><ImageInput value={s.about_image} onChange={(v) => set("about_image", v)} testId="admin-settings-about-image" /></div>}
          {title === "Pricing Page" && (
            <div className="mt-4 space-y-4">
              <div><label className="label">Gang sheet price image</label><ImageInput value={s.gang_sheet_image} onChange={(v) => set("gang_sheet_image", v)} testId="admin-settings-gang-image" /></div>
              <div><label className="label">Pricing notes (one per line)</label><textarea className="field min-h-[120px]" value={s.pricing_notes.join("\n")} onChange={(e) => set("pricing_notes", e.target.value.split("\n"))} data-testid="admin-settings-pricing_notes" /></div>
            </div>
          )}
        </section>
      ))}
      <section className="panel p-6">
        <h2 className="red-title mb-4 text-xl">Policies</h2>
        <PoliciesEditor policies={s.policies} onChange={(v) => set("policies", v)} />
      </section>
      <ListsEditor s={s} set={set} />
      <GangSettings s={s} set={set} />
      <PasswordForm />
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-black/90 px-6 py-3 backdrop-blur lg:left-60">
        <button className="btn-red !py-2" onClick={saveAll} disabled={busy} data-testid="admin-settings-save-button">{busy ? "Saving..." : "Save All Settings"}</button>
      </div>
    </div>
  );
}
